/**
 * THE SYSTEM LOGIN.
 *
 * One form, for everybody. Canon 1.1 is explicit that there is no second
 * "Master" login mechanism, because "a second, parallel 'Master' login would
 * double the attack surface for exactly the accounts that need protecting the
 * most". A Support Centre agent and the Founder type into the same two fields;
 * what differs is the menu on the other side.
 *
 * There is no "sign up" here, and there must not be one. Staff accounts are
 * provisioned by somebody already inside (People & Access), which is the
 * accountability chain canon Part Three encodes as created_by_staff_id --
 * "always someone, never null".
 *
 * Built from src/ui.jsx, the consumer app's own component set, so this screen
 * is the same product a member sees. The words carry the distinction the
 * colour used to -- "Internal system. Your access is recorded." -- and the
 * name is "Orchid Portal" here and everywhere inside, never "Staff Portal":
 * the people using it work here, and being told so on every screen is noise.
 */

import { useState } from 'react';
import { supabase, supabaseConfigStatus } from './lib/supabaseClient';
import { emailProblem, isStaffEmail } from './lib/staffCredentials';
import { deliveryProblem } from './lib/email';
import {
  O, SANS, DISPLAY, Card, PrimaryCTA, Field, textInputStyle, Notice, TrustFooter,
} from './ui';

export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const config = supabaseConfigStatus();
  const domainProblem = touched ? emailProblem(email) : null;

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setTouched(true);
    if (emailProblem(email)) return;
    if (!password) { setError('Enter your password.'); return; }

    setBusy(true);
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      // ONE MESSAGE FOR BOTH FAILURES, DELIBERATELY.
      //
      // "No such account" and "wrong password" are different facts, and telling
      // them apart hands an attacker a way to enumerate which Orchid addresses
      // are staff. Somebody who genuinely mistyped is mildly inconvenienced;
      // somebody guessing learns nothing.
      if (authError) setError('That email address and password did not match a staff account.');
    } catch {
      setError('Orchid could not reach the network just now. Nothing has changed.');
    } finally {
      // finally, not a trailing statement: an exception above must still
      // release the button, or the form spins for ever.
      setBusy(false);
    }
  }

  /**
   * "The 'I have forgotten my password' link doesn't work."
   *
   * It didn't, in two separate ways, and both are fixed here.
   *
   * ONE -- this function had a try/finally and no catch and no error check. A
   * network failure or an SMTP failure threw past it, setSent(true) never ran,
   * and the button appeared to do nothing at all. There was no path by which a
   * person could learn anything had gone wrong.
   *
   * TWO -- redirectTo pointed at /reset, and the router had no /reset route.
   * Anyone who did receive a link landed on the catch-all and was bounced to
   * the portal carrying a recovery session, without ever being asked for a new
   * password. The route now exists (see App.jsx and ResetPassword.jsx).
   *
   * What has NOT changed: success says the same thing whether or not that
   * address is a real staff account, for the same reason the sign-in error is
   * single. A different message here would enumerate staff addresses just as
   * well as a different sign-in error would.
   */
  async function sendReset() {
    if (emailProblem(email)) { setTouched(true); return; }
    setBusy(true);
    setError(null);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo: `${window.location.origin}/reset` },
      );
      if (resetError) {
        // A send failure is Orchid's problem, not the person's, and it is not
        // an enumeration risk: it says nothing about whether the address
        // exists. Naming it is the difference between "the link is broken"
        // and "email is not configured on this project".
        setError(deliveryProblem(resetError));
        return;
      }
      setSent(true);
    } catch {
      setError('Orchid could not reach the network just now. Nothing has changed.');
    } finally {
      setBusy(false);
    }
  }

  const [sent, setSent] = useState(false);

  return (
    <main style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '48px 20px', background: O.canvas, fontFamily: SANS,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* The mark from the main website, not a word set in Georgia. Somebody
            arriving here should recognise the company before they read
            anything. "Orchid Portal", never "Staff Portal" -- the founder's
            ruling, and it holds inside the portal too. */}
        <div style={{ marginBottom: 26, textAlign: 'center' }}>
          <img
            src="/images/orchid-logo-green.png"
            alt="Orchid"
            width={64}
            height={64}
            style={{ borderRadius: '50%', objectFit: 'cover', display: 'inline-block' }}
            onError={(e) => { e.target.style.visibility = 'hidden'; }}
          />
          <div style={{
            marginTop: 10, fontFamily: DISPLAY, fontSize: 20, color: O.green,
            letterSpacing: '-0.01em',
          }}>Orchid Portal</div>
        </div>

        <Card>
          {!config.ok && <Notice tone="warning">{config.detail}</Notice>}

          {sent ? (
            <>
              <h1 style={{ fontSize: 17, fontWeight: 700, color: O.ink, marginBottom: 8 }}>
                Check that inbox.
              </h1>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: O.inkMuted }}>
                If a staff account uses that address, a link to set a new password is on
                its way. The link is single-use and expires.
              </p>
            </>
          ) : (
            <form onSubmit={submit} noValidate>
              <h1 style={{ fontSize: 17, fontWeight: 700, color: O.ink, marginBottom: 4 }}>
                Sign in
              </h1>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: O.inkMuted, marginBottom: 18 }}>
                Internal system. Your access is recorded.
              </p>

              <Field label="Orchid email address" htmlFor="staff-email" error={domainProblem}>
                <input
                  id="staff-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder="name@orchidholdings.co.za"
                  aria-invalid={Boolean(domainProblem)}
                  style={textInputStyle(Boolean(domainProblem))}
                />
              </Field>

              <Field label="Password" htmlFor="staff-password">
                <input
                  id="staff-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={textInputStyle(false)}
                />
              </Field>

              {error && <Notice tone="critical">{error}</Notice>}

              <div style={{ marginTop: 18 }}>
                <PrimaryCTA
                  type="submit"
                  busy={busy}
                  disabled={busy || !isStaffEmail(email) || !password}
                >
                  Sign in
                </PrimaryCTA>
              </div>

              <button
                type="button"
                onClick={sendReset}
                style={{
                  marginTop: 14, background: 'none', border: 'none', padding: 0,
                  font: 'inherit', fontSize: 12.5, color: O.green, cursor: 'pointer',
                  textDecoration: 'underline', textUnderlineOffset: 3,
                }}
              >
                I have forgotten my password
              </button>
            </form>
          )}
        </Card>

        {/* The paragraph that used to sit here is gone on the founder's
            ruling -- "Remove the writing beneath the sign-in block; it's
            unnecessary." Nothing it said was load-bearing: there is no sign-up
            control to explain away. */}
        <TrustFooter style={{ marginTop: 22 }} />
      </div>
    </main>
  );
}
